import React, { useState, useEffect } from 'react'
import {
	makeStyles,
	Button,
	Card,
	CardHeader,
	CardContent,
	Checkbox,
	ListItemText,
	TextField,
	MenuItem,
	Typography,
	IconButton,
	Divider
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'
import { AddOutlined } from '@material-ui/icons'
import AddGuardianDialog from '../../Dialogs/AddGuardian'
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation'
import StyledAlert from '../../Common/StyledAlert'
import { v4 as uuid } from 'uuid'
import clsx from 'clsx'
import { useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { purple } from '../../../utils/colors'

const useStyles = makeStyles((theme) => ({
	grid: {
		display: 'grid',
		gridGap: '23px',
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		marginBottom: 36
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			},
			'& .MuiFormControl-root': {
				width: '424px !important'
			},
			'& .MuiSelect-select': {
				width: '424px !important'
			}
		}
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	},
	guardianRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		marginBottom: 24,
		'& .MuiFilledInput-root': {
			border: 'none',
			borderBottom: '2px solid #F9F9F9',
			'& .Mui-disabled': {
				borderTop: 'none'
			}
		},
		'& .MuiFilledInput-underline.Mui-disabled:before': {
			border: 'none'
		},
		'& .MuiFormLabel-root': {
			color: '#777679',
			fontSize: 14,
			fontWeight: '700'
		},
		'& .MuiSelect-selectMenu': {
			border: '1px solid #D0CFD2 !important',
			borderBottom: '2px solid #a2a1a4 !important',
			borderTopLeftRadius: 6,
			borderTopRightRadius: 6
		}
	},
	deleteButton: {
		border: '1px solid #DC2A2A',
		height: 38,
		width: 38,
		padding: 5,
		'& svg': {
			height: 16
		}
	},
	hyperLink: {
		textDecoration: 'none',
		color: purple[500],
		fontSize: '16px',
		fontWeight: '800'
	}
}))

const Other = (props) => {
	const classes = useStyles()
	const constants = useSelector((state) => state.general.constants.constants)
	const [guardians, setGuardians] = useState([])
	const [isDeleteShown, setDeleteShown] = useState(false)
	const [selectedGuardian, setSelectedGuardian] = useState(null)
	const [addShown, setAddShown] = useState(false)
	const facilities = useSelector((state) => state.specific.facilities.facilities)



	let consultationStatuses = ['unscheduled', 'scheduled', 'completed']

	let { household, setHousehold, updateGuardian } = props

	const [defaultFacility, setDefaultFacility] = useState(null)

	useEffect(() => {
		if (facilities?.length > 0 && household?._id) {
			const fac = facilities.find(f => f._id == household.default_facility_id)
			if (fac) {
				setDefaultFacility(fac)
			}
		}
	}, [facilities, household?._id])


	const formatPhoneNumber = (phoneNumberString) => {
		var cleaned = ('' + phoneNumberString).replace(/\D/g, '')
		var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
		if (match) {
			return '(' + match[1] + ') ' + match[2] + '-' + match[3]
		}
		return phoneNumberString
	}

	useEffect(() => {
		if (household?.account_guardians) {
			let guards = [...household.account_guardians]
			if (household?.pet_guardians) {
				guards = [...guards, ...household.pet_guardians]
			}
			guards.sort((a, b) => {
				if (a.primary && !b.primary) return -1
				if (!a.guardian_id && b.guardian_id) return -1
				if (a.guardian_id && b.guardian_id) return 0
			})

			guards = guards.map((a) => {
				if (!a.guardian_id) {
					return {
						...a,
						phone_number: formatPhoneNumber(a.phone_number),
						relationship: a.primary ? 'Primary' : 'Non-primary',
						status: a.primary || a.is_guardian ? 'Permitted' : 'Not Permitted'
					}
				}
				return {
					...a,
					phone_number: formatPhoneNumber(a.phone_number),
					relationship: 'Guest',
					status: 'Permitted'
				}
			})

			setGuardians(guards)
		}
	}, [household])

	const updateHousehold = (field, value) => {
		if (household) {
			setHousehold({ ...household, [field]: value })
		}
	}

	const remove = () => {
		let pet_guardians = household.pet_guardians.filter(
			(guard) => guard.guardian_id !== selectedGuardian.guardian_id
		)
		updateGuardian(pet_guardians)
		setDeleteShown(false)
		setSelectedGuardian(null)
	}

	const addGuardian = (g) => {
		let bod = { ...g, guardian_id: uuid() }
		let guards = household.pet_guardians || []
		let pet_guardians = [...guards, bod]
		updateGuardian(pet_guardians)
	}

	return (
		<Card className={classes.card}>
			<CardHeader title="Other" />
			<CardContent>
				<div className={clsx([classes.grid, classes.gridDouble])}>
					<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
						<TextField
							label="Consultation Status"
							variant="filled"
							fullWidth
							select
							style={{ textTransform: 'capitalize' }}
							value={household.consultation_status}
							onChange={(e) => updateHousehold('consultation_status', e.target.value)}>
							{consultationStatuses ? (
								consultationStatuses.map((status) => (
									<MenuItem key={status} style={{ textTransform: 'capitalize' }} value={status}>
										{status}
									</MenuItem>
								))
							) : (
								<MenuItem>No Appointment Statuses</MenuItem>
							)}
						</TextField>
					</div>
					<div className={clsx([classes.fieldWrapper, 'field-wrapper'])} />
				</div>
				<div className={classes.guardians}>
					<Typography variant="header5" style={{ fontSize: 16, fontWeight: '800', marginTop: 12 }}>
						Pet Guardians
					</Typography>
					<Typography
						variant="body2"
						style={{ fontSize: 16, fontWeight: '600', marginTop: 6, marginBottom: 24 }}>
						All Household Contacts are automatically included in this Pet Guardians list and are
						permitted to pick up the clients’ dog(s) after appointments. Please ensure their phone
						numbers are correct for pick-up.
					</Typography>
					<div className={classes.guardianRows}>
						{guardians.map((g) => {
							return (
								<GuardianRow
									guardian={g}
									updateHousehold={updateHousehold}
									household={household}
									setDeleteShown={setDeleteShown}
									setSelectedGuardian={setSelectedGuardian}
								/>
							)
						})}
					</div>
					<Button
						variant="outlined"
						style={{ marginBottom: '24px' }}
						startIcon={<AddOutlined style={{ fill: '#6B4AD6' }} />}
						onClick={() => setAddShown(true)}
						color="secondary">
						PET GUARDIAN
					</Button>
				</div>
				{
					defaultFacility?.terms_and_conditions_signature_required && (
						<div>
							<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
								<Typography variant="header5" style={{ fontSize: 16, fontWeight: '800', marginTop: 12 }}>
									Terms and Conditions Signature
								</Typography>
								{!!household?.terms_and_conditions_accepted ? (
									<div style={{ marginTop: 12 }}>
										{household.terms_and_conditions_signature_url ? (
											<img
												style={{ height: 200, border: '1px solid #eee' }}
												src={household.terms_and_conditions_signature_url}
											/>
										) : (
											<StyledAlert
												type="error"
												text="Client has signed, but signature url is missing!"
												style={{ marginTop: 12 }}
											/>
										)}
										<div style={{ marginTop: 6 }}>
											<span>
												{household.terms_and_conditions_details ? (
													<a
														className={classes.hyperLink}
														target="_blank"
														href={
															household.terms_and_conditions_details.pdf_url
														}>{`Version ${household.terms_and_conditions_details.version}`}</a>
												) : (
													'Version signed unavailable.'
												)}
											</span>
											<span>{' | '}</span>
											<span>
												Signed{' '}
												{household.terms_and_conditions_accepted_at
													? moment(household.terms_and_conditions_accepted_at)
														.tz('America/New_York')
														.format('MMMM DD, YYYY')
													: 'Date is missing!'}
											</span>
										</div>
									</div>
								) : (
									<StyledAlert
										type="warning"
										text="Client has not signed the authorization for pet care services."
										style={{ marginTop: 12 }}
									/>
								)}
							</div>
						</div>
					)
				}
			</CardContent>
			<DeleteConfirmation
				isShown={isDeleteShown}
				onConfirmDelete={remove}
				confirmButtonText="REMOVE"
				title="Remove Pet Guardian"
				body="Are you sure you want to remove this person as a Pet Guardian? They will no longer be allowed to pick up and drop-off this client’s pet(s) for appointments."
				setIsShown={setDeleteShown}
			/>
			<AddGuardianDialog add={addGuardian} isShown={addShown} setIsShown={setAddShown} />
		</Card>
	)
}

const GuardianRow = ({ guardian, updateHousehold, household, setDeleteShown, setSelectedGuardian }) => {
	const classes = useStyles()

	const canEdit = guardian._id && !guardian.guardian_id && !guardian.primary

	const { pet_guardians, account_guardians } = household
	const updateGuardian = (g) => {
		if (guardian.guardian_id) {
			// this is handled in parent
		} else {
			let accts = account_guardians.map((a) => {
				if (a._id === guardian._id) {
					return {
						...a,
						is_guardian: g === 'Permitted' ? true : false
					}
				}
				return a
			})

			// updateHousehold('accounts', accts)
			updateHousehold('account_guardians', accts)
		}
	}
	// console.log(guardian.status)

	return (
		<div className={classes.guardianRow}>
			<TextField
				disabled={true}
				variant="filled"
				label="Contact Type"
				value={guardian.relationship}
				style={{ width: '22%', marginRight: 16, minWidth: 100, border: 'none' }}
			/>
			<TextField
				disabled={true}
				variant="filled"
				label="Contact Name"
				value={`${guardian.first_name} ${guardian.last_name}`}
				style={{ width: '22%', marginRight: 16, minWidth: 100, border: 'none' }}
			/>
			<TextField
				disabled={true}
				variant="filled"
				label="Contact Number"
				value={guardian.phone_number}
				style={{ width: '22%', marginRight: 16, minWidth: 100, border: 'none' }}
			/>
			{canEdit ? (
				<TextField
					variant="filled"
					label="Guardian Status"
					value={guardian.status}
					select
					inputProps={{
						style: {
							border: '1px solid #A2A1A4'
						}
					}}
					onChange={(e) => {
						updateGuardian(e.target.value)
					}}
					style={{ width: '22%', marginRight: 16, minWidth: 100, border: 'none' }}>
					<MenuItem value="Permitted">Permitted</MenuItem>
					<MenuItem value="Not Permitted">Not Permitted</MenuItem>
				</TextField>
			) : (
				<TextField
					disabled={true}
					variant="filled"
					label="Guardian Status"
					value={guardian.status}
					style={{ width: '22%', marginRight: 16, minWidth: 100, border: 'none' }}
				/>
			)}
			{guardian.guardian_id && (
				<IconButton
					onClickCapture={() => {
						setDeleteShown(true)
						setSelectedGuardian(guardian)
					}}
					variant="outlined"
					className={classes.deleteButton}
					onClick={() => { }}>
					<CloseIcon style={{ fill: '#DC2A2A' }} />
				</IconButton>
			)}
		</div>
	)
}

export default Other
